import { Injectable } from '@angular/core';
import { TealiumTrackingService } from '../../../../core/services/tealium-tracking.service';
import { UtagHelperService } from '../../../../core/services/utag-helper.service';
import { IDocumentTypeText } from '../../../../models/document/document-type-text.model';
import { MydocumentsItemAdapterService } from '../../../../core/http/adapters/mydocuments-item/mydocuments-item.service';
import { TrackingElement } from '../../enum/tracking-element.enum';
import { SessionType } from '../../enum/session-type.enum';

@Injectable({
  providedIn: 'root',
})
export class TrackingHelperService {
  private app_name!: string;
  private site_country!: string;
  private site_language!: string;

  public constructor(
    private tealium: TealiumTrackingService,
    private utagHelperService: UtagHelperService,
    private myDocumentsItem: MydocumentsItemAdapterService
  ) {
    this.app_name = this.utagHelperService.getAppName();
    this.site_country = this.utagHelperService.getCountry();
    this.site_language = this.utagHelperService.getLanguage();
  }

  public getSiteLanguage(): string {
    let language = 'EN';
    if (this.site_language) {
      language = this.site_language.toUpperCase();
    }
    return language;
  }

  public trackResetFileFolder(isFile: boolean, sessionType: string): void {
    const contentLinkText: string = isFile
      ? TrackingElement.ACTION_RESET_FILE
      : TrackingElement.ACTION_RESET_FOLDER;
    const eventId = this.getEventId(
      sessionType,
      TrackingElement.PAGE_EXPORT,
      contentLinkText
    );
    this.tealium.link({
      event_name: TrackingElement.PART_CONTENT_LINK,
      event_linkname: contentLinkText,
      event_subtype: contentLinkText,
      event_id: eventId,
    });
  }

  public trackSelectAllProducts(
    isSelected: boolean,
    sessionType: string
  ): void {
    let subType: string = isSelected
      ? TrackingElement.SUBTYPE_CHECKED
      : TrackingElement.SUBTYPE_UNCHECKED;

    const eventId = this.getEventId(
      sessionType,
      TrackingElement.PAGE_PRODUCT,
      TrackingElement.ACTION_SELECT_ALL,
      TrackingElement.PART_CHECKBOX
    );
    this.tealium.link({
      event_name: TrackingElement.PART_CHECKBOX,
      event_linkname: TrackingElement.ACTION_SELECT_ALL,
      event_subtype: subType,
      event_id: eventId,
    });
  }

  public trackNextStepOnProductPage(sessionType: string): void {
    const eventId = this.getEventId(
      sessionType,
      TrackingElement.PAGE_PRODUCT,
      TrackingElement.ACTION_NEXT_STEP
    );
    this.tealium.link({
      event_name: TrackingElement.PART_CONTENT_LINK,
      event_linkname: TrackingElement.ACTION_NEXT_STEP,
      event_subtype: TrackingElement.SUBTYPE_NEXT_STEP,
      event_id: eventId,
    });
  }

  public trackNextStepOnDocumentTypePage(sessionType: string): void {
    const eventId = this.getEventId(
      sessionType,
      TrackingElement.PAGE_DOCUMENT,
      TrackingElement.ACTION_NEXT_STEP
    );
    this.tealium.link({
      event_name: TrackingElement.PART_CONTENT_LINK,
      event_linkname: TrackingElement.ACTION_NEXT_STEP,
      event_subtype: TrackingElement.SUBTYPE_NEXT_STEP,
      event_id: eventId,
    });
  }

  public trackBackStepOnDocumentTypePage(sessionType: string): void {
    const eventId = this.getEventId(
      sessionType,
      TrackingElement.PAGE_DOCUMENT,
      TrackingElement.ACTION_BACK
    );
    this.tealium.link({
      event_name: TrackingElement.PART_CONTENT_LINK,
      event_linkname: TrackingElement.ACTION_BACK,
      event_subtype: TrackingElement.SUBTYPE_BACK,
      event_id: eventId,
    });
  }

  public trackBackStepOnExportSettingsPage(sessionType: string): void {
    const eventId = this.getEventId(
      sessionType,
      TrackingElement.PAGE_EXPORT,
      TrackingElement.ACTION_BACK
    );
    this.tealium.link({
      event_name: TrackingElement.PART_CONTENT_LINK,
      event_linkname: TrackingElement.ACTION_BACK,
      event_subtype: TrackingElement.SUBTYPE_BACK,
      event_id: eventId,
    });
  }

  public trackShowMoreAndLessDocumentTypes(
    sessionType: string,
    showMoreDocuments: boolean
  ): void {
    if (!showMoreDocuments) {
      const eventId = this.getEventId(
        sessionType,
        TrackingElement.PAGE_DOCUMENT,
        TrackingElement.ACTION_SHOW_MORE,
        TrackingElement.PART_SHOW_MORE
      );
      this.tealium.link({
        event_name: TrackingElement.PART_SHOW_MORE,
        event_linkname: TrackingElement.ACTION_SHOW_MORE,
        event_subtype: TrackingElement.SUBTYPE_MORE,
        event_id: eventId,
      });
    } else {
      const eventId = this.getEventId(
        sessionType,
        TrackingElement.PAGE_DOCUMENT,
        TrackingElement.ACTION_SHOW_LESS,
        TrackingElement.PART_SHOW_MORE
      );
      this.tealium.link({
        event_name: TrackingElement.PART_SHOW_MORE,
        event_linkname: TrackingElement.ACTION_SHOW_LESS,
        event_subtype: TrackingElement.SUBTYPE_LESS,
        event_id: eventId,
      });
    }
  }

  public trackSaveUserEntries(
    sessionType: string,
    docTypesTrackingSemi: string,
    docTypesTrackingPipe: string,
    exportSettingsComb: string,
    exportSettingsSeparate: string,
    levelUsedCounter: string,
    languageIds: string[],
    productPosNumbers: string[],
    saveSettingsAsDefault: boolean
  ): void {
    const selectedProductsTracking: string = productPosNumbers.join(';');
    const docLanguagesTracking: string = languageIds.join('|');

    const eventId = this.getEventId(
      sessionType,
      TrackingElement.PAGE_EXPORT,
      TrackingElement.ACTION_CREATE
    );

    this.tealium.link({
      event_name: TrackingElement.PART_CONTENT_LINK,
      event_linkname: TrackingElement.ACTION_CREATE,
      event_subtype: TrackingElement.SUBTYPE_CREATE,
      event_order_code: selectedProductsTracking,
      event_download_language: docLanguagesTracking,
      event_download_type: docTypesTrackingSemi,
      event_download_type_comb: docTypesTrackingPipe,
      folder_file_comb: exportSettingsComb,
      folder_file_separate: exportSettingsSeparate,
      checkbox_status: saveSettingsAsDefault
        ? TrackingElement.SUBTYPE_CHECKED
        : TrackingElement.SUBTYPE_UNCHECKED,
      level_used_counter: levelUsedCounter,
      event_id: eventId,
    });
  }

  public trackChangeStep(sessionType: string, selectedIndex: number): void {
    if (selectedIndex === 2) {
      const pageName = this.getPageName(
        sessionType,
        TrackingElement.PAGE_EXPORT
      );

      this.tealium.view({
        app_name: this.app_name,
        site_country: this.site_country,
        site_language: this.site_language,
        site_h1: TrackingElement.PART_MYEUH,
        site_h2: this.getDocumentType(sessionType),
        site_h3: TrackingElement.PART_WIZARD,
        site_h4: TrackingElement.PAGE_EXPORT,
        login_status_per_page: this.getLoggedInText(),
        customer_role: this.myDocumentsItem.customer_role,
        customer_subrole: this.myDocumentsItem.customer_subrole,
        login_status: this.getLoggedInText(),
        page_name: pageName,
      });
    }
    if (selectedIndex === 1) {
      const pageName = this.getPageName(
        sessionType,
        TrackingElement.PAGE_DOCUMENT
      );

      this.tealium.view({
        app_name: this.app_name,
        site_country: this.site_country,
        site_language: this.site_language,
        site_h1: TrackingElement.PART_MYEUH,
        site_h2: this.getDocumentType(sessionType),
        site_h3: TrackingElement.PART_WIZARD,
        site_h4: TrackingElement.PAGE_DOCUMENT,
        login_status_per_page: this.getLoggedInText(),
        customer_role: this.myDocumentsItem.customer_role,
        customer_subrole: this.myDocumentsItem.customer_subrole,
        login_status: this.getLoggedInText(),
        page_name: pageName,
      });
    }
  }

  public trackViewMyDocuments(sessionType: string): void {
    const pageName = this.getPageName(
      sessionType,
      TrackingElement.PAGE_PRODUCT
    );

    this.tealium.view({
      app_name: this.app_name,
      site_country: this.site_country,
      site_language: this.site_language,
      site_h1: TrackingElement.PART_MYEUH,
      site_h2: this.getDocumentType(sessionType),
      site_h3: TrackingElement.PART_WIZARD,
      site_h4: TrackingElement.PAGE_PRODUCT,
      login_status_per_page: this.getLoggedInText(),
      customer_role: this.myDocumentsItem.customer_role,
      customer_subrole: this.myDocumentsItem.customer_subrole,
      login_status: this.getLoggedInText(),
      page_name: pageName,
    });
  }

  private getDocumentType(sessionType: string): string {
    return sessionType === SessionType.QUOTE
      ? TrackingElement.PART_QUOTES
      : TrackingElement.PART_ORDERS;
  }

  private getLoggedInText(): string {
    const isLoggedIn = document.getElementsByClassName('Is-logged-in');

    return isLoggedIn ? TrackingElement.PART_LOGGIN : '';
  }

  private getEventId(
    sessionType: string,
    pageName: string,
    eventValue: string,
    eventName: string = TrackingElement.PART_CONTENT_LINK
  ): string {
    return (
      this.getPageName(sessionType, pageName) +
      '.' +
      eventName +
      '.' +
      eventValue
    );
  }

  private getPageName(sessionType: string, pageName: string): string {
    return (
      this.app_name +
      '.' +
      this.site_country +
      '.' +
      this.site_language +
      '.' +
      TrackingElement.PART_MYEUH +
      '.' +
      this.getDocumentType(sessionType) +
      '.' +
      TrackingElement.PART_WIZARD +
      '.' +
      pageName
    );
  }
}
